import type { BrandDefinition } from "@school-sales-ai/shared";
import { LEGAL_CONTACT_EMAIL, LEGAL_ENTITY, LEGAL_PROSE_CLASS } from "./LegalPageShell";

type TermsContentProps = {
  brand: BrandDefinition;
  lastUpdated: string;
};

function ContactLink(): React.JSX.Element {
  return <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>;
}

/**
 * The Términos y condiciones every CRM user accepts — the ONLY copy of the text.
 * Rendered by the public page and inside the acceptance gate, so the two can
 * never show different documents.
 *
 * Changing a word here means bumping `TERMS_VERSION` (and appending it to
 * `KNOWN_TERMS_VERSIONS`) in packages/shared: an acceptance record names a
 * version, and a version whose text changed underneath it names nothing.
 */
export function TermsContent({ brand, lastUpdated }: TermsContentProps): React.JSX.Element {
  const platform = brand.name;
  return (
    <article className={LEGAL_PROSE_CLASS}>
      <h1>Términos y Condiciones de Uso</h1>

      <p>
        Estos Términos y Condiciones (los «Términos») regulan el acceso y uso de
        la plataforma {platform} (la «Plataforma»), operada por {LEGAL_ENTITY}{" "}
        («nosotros»). <strong>Última actualización: {lastUpdated}.</strong>
      </p>

      <h2>1. Aceptación</h2>
      <p>
        Al marcar la casilla de aceptación y utilizar la Plataforma, usted declara
        haber leído y aceptado estos Términos. Si no está de acuerdo con ellos, no
        debe utilizar la Plataforma. Registramos la fecha, la versión aceptada y
        los datos técnicos de la conexión desde la que se aceptó, como evidencia
        de la aceptación.
      </p>

      <h2>2. Definiciones</h2>
      <ul>
        <li>
          <strong>Plataforma:</strong> el sistema {platform}, que incluye el panel
          de administración (CRM), el asistente virtual y sus canales (sitio web y
          WhatsApp).
        </li>
        <li>
          <strong>Organización:</strong> la institución educativa, comercio o
          empresa que contrató la Plataforma y en cuyo nombre usted la utiliza.
        </li>
        <li>
          <strong>Usuario:</strong> la persona a la que se le otorgó una cuenta
          para acceder a la Plataforma, sea personal de la Organización o
          nuestro.
        </li>
        <li>
          <strong>Prospecto:</strong> la persona que conversa con el asistente
          virtual o con el personal de la Organización para pedir información,
          inscribirse o comprar.
        </li>
        <li>
          <strong>Asistente virtual:</strong> el agente basado en inteligencia
          artificial que conversa con los Prospectos en nombre de la
          Organización.
        </li>
      </ul>

      <h2>3. Cuentas y acceso</h2>
      <ul>
        <li>
          Su cuenta es <strong>personal e intransferible</strong>. No comparta su
          contraseña ni permita que otra persona use su sesión.
        </li>
        <li>
          Usted es responsable de la actividad que se realice con su cuenta.
          Notifíquenos de inmediato a <ContactLink /> si sospecha de un acceso no
          autorizado.
        </li>
        <li>
          Los permisos de su cuenta los define su Organización. Utilice
          únicamente las funciones y la información a las que se le dio acceso.
        </li>
      </ul>

      <h2>4. Uso permitido</h2>
      <p>
        Usted se compromete a usar la Plataforma únicamente para atender a los
        Prospectos de su Organización y administrar su operación, y a{" "}
        <strong>no</strong>:
      </p>
      <ul>
        <li>
          Enviar mensajes no solicitados, engañosos o masivos a personas que no
          dieron su consentimiento, ni volver a contactar por campañas a quien
          pidió dejar de recibirlas.
        </li>
        <li>
          Incumplir las políticas de WhatsApp y de Meta, en particular las de
          mensajería comercial y plantillas.
        </li>
        <li>
          Cargar en la base de conocimiento, el catálogo o las plantillas
          contenido ilícito, falso, discriminatorio o que infrinja derechos de
          terceros.
        </li>
        <li>
          Intentar manipular al asistente virtual para que actúe fuera de sus
          instrucciones, o intentar vulnerar la seguridad de la Plataforma.
        </li>
        <li>
          Acceder o intentar acceder a información de otras organizaciones, ni
          extraer datos de la Plataforma por medios automatizados.
        </li>
        <li>
          Copiar, modificar, descompilar o aplicar ingeniería inversa a la
          Plataforma.
        </li>
      </ul>

      <h2>5. Asistente virtual</h2>
      <ul>
        <li>
          El asistente virtual genera sus respuestas con inteligencia artificial
          a partir de la información que la Organización carga en la Plataforma.{" "}
          <strong>Puede equivocarse</strong>.
        </li>
        <li>
          La Organización es responsable de que su base de conocimiento, su
          catálogo, sus precios y sus promociones sean correctos y estén
          actualizados.
        </li>
        <li>
          El asistente no sustituye el criterio del personal de la Organización.
          Las decisiones sobre admisiones, ventas, precios y compromisos con los
          Prospectos corresponden a la Organización.
        </li>
      </ul>

      <h2>6. Datos personales de los Prospectos</h2>
      <p>
        La información de los Prospectos que usted consulta en la Plataforma
        contiene datos personales. Usted se compromete a tratarla únicamente
        para atender la solicitud de información, admisión o compra del
        Prospecto, a no divulgarla ni usarla para fines distintos, y a respetar
        la Ley Federal de Protección de Datos Personales en Posesión de los
        Particulares. El tratamiento de esos datos se describe en el{" "}
        {/* Relative, and in a new tab: this text also renders inside the
            acceptance gate, which must not be navigated away from. */}
        <a href={brand.privacyNoticePath} target="_blank" rel="noreferrer">
          Aviso de Privacidad
        </a>
        .
      </p>
      <p>
        Sus propios datos como Usuario (nombre, correo electrónico y registro de
        actividad) se tratan para administrar su cuenta y la seguridad de la
        Plataforma.
      </p>

      <h2>7. Contenido de la Organización</h2>
      <p>
        La información que la Organización carga en la Plataforma (base de
        conocimiento, catálogo, documentos, plantillas e imágenes) sigue siendo
        de la Organización. Nos otorga únicamente el permiso necesario para
        almacenarla, procesarla y mostrarla con el fin de prestar el servicio.
      </p>

      <h2>8. Servicios de terceros</h2>
      <p>
        La Plataforma se apoya en servicios de terceros, entre ellos WhatsApp
        (Meta Platforms), proveedores de infraestructura en la nube, de
        inteligencia artificial y de correo electrónico. El uso de WhatsApp está
        sujeto además a las condiciones de Meta, y Meta cobra por los mensajes
        de plantilla; la Plataforma muestra el costo estimado de un envío masivo
        antes de confirmarlo. No somos responsables de las fallas,
        suspensiones o cambios de esos servicios.
      </p>

      <h2>9. Propiedad intelectual</h2>
      <p>
        La Plataforma, su código, diseño, marcas y logotipos son propiedad de{" "}
        {LEGAL_ENTITY} o de sus licenciantes. Estos Términos no le otorgan ningún
        derecho sobre ellos, salvo el de usar la Plataforma conforme a lo aquí
        previsto.
      </p>

      <h2>10. Disponibilidad y cambios del servicio</h2>
      <p>
        Procuramos que la Plataforma esté disponible de forma continua, pero
        puede interrumpirse por mantenimiento, actualizaciones o causas ajenas a
        nosotros. Podemos agregar, modificar o retirar funciones para mejorar el
        servicio.
      </p>

      <h2>11. Relación con el contrato de la Organización</h2>
      <p>
        Las condiciones comerciales del servicio (precio, plan, vigencia y
        niveles de servicio) se rigen por el contrato celebrado entre la
        Organización y {LEGAL_ENTITY}. Si existe alguna contradicción entre ese
        contrato y estos Términos, <strong>prevalece el contrato</strong>.
      </p>

      <h2>12. Limitación de responsabilidad</h2>
      <p>
        En la medida en que la ley lo permita, {LEGAL_ENTITY} no será responsable
        de daños indirectos, lucro cesante ni pérdida de oportunidades derivados
        del uso de la Plataforma, de las respuestas del asistente virtual, del
        contenido cargado por la Organización o de fallas de servicios de
        terceros.
      </p>

      <h2>13. Suspensión y terminación de cuentas</h2>
      <p>
        La Organización puede desactivar su cuenta en cualquier momento.
        Nosotros podemos suspenderla si incumple estos Términos o si su uso pone
        en riesgo la seguridad de la Plataforma, de otras organizaciones o de los
        Prospectos.
      </p>

      <h2>14. Modificaciones</h2>
      <p>
        Podemos modificar estos Términos. Cuando lo hagamos, publicaremos la
        nueva versión en esta página y le pediremos que la acepte la próxima vez
        que ingrese a la Plataforma. Si no la acepta, no podrá seguir
        utilizándola.
      </p>

      <h2>15. Legislación aplicable</h2>
      <p>
        Estos Términos se rigen por las leyes federales de los Estados Unidos
        Mexicanos.
      </p>

      <h2>16. Contacto</h2>
      <p>
        Para cualquier duda sobre estos Términos, escríbanos a <ContactLink />.
      </p>

      <p>
        <strong>Última actualización: {lastUpdated}.</strong>
      </p>
    </article>
  );
}
